// ABOUTME: Debug drawer rendering the variant pricing explain trace for a configuration.
// ABOUTME: Shows the price, the per-rule trace, and — when pricing throws — the error and surviving trace.

import {
    Box,
    Chip,
    CircularProgress,
    CloseIcon,
    Divider,
    Drawer,
    IconButton,
    Stack,
    Typography,
} from '@ds/coolshop';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';

import { KeyEmitterSearch } from './KeyEmitterSearch';
import { PricingErrorPanel } from './PricingErrorPanel';
import { TraceNodeList } from './TraceNodeList';
import { useExplainPrice } from './useExplainPrice';

interface PricingTraceDrawerProps {
    open: boolean;
    configurationJson: string;
    onClose: () => void;
}

export function PricingTraceDrawer({ open, configurationJson, onClose }: PricingTraceDrawerProps) {
    const { formatMessage } = useIntl();
    const { explain, loading, error, result } = useExplainPrice();

    useEffect(() => {
        if (!open || !configurationJson) return;
        explain(configurationJson);
    }, [open, configurationJson, explain]);

    return (
        <Drawer anchor="right" width="70%" open={open} onClose={onClose}>
            <Stack height="100vh" gap={2} padding={2}>
                <Stack direction="row" alignItems="center" gap={1}>
                    <Box flex={1}>
                        <Typography color="primary" variant="h5">
                            {formatMessage({
                                id: 'cpq.pricing-trace.title',
                                defaultMessage: 'Explain price',
                            })}
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Stack>

                {/* Reverse lookup works standalone from the traced configuration: paste any price key
                    to find the rules that emit it. Kept above the trace so it is always reachable. */}
                <KeyEmitterSearch />
                <Divider />

                {loading && (
                    <Box display="flex" justifyContent="center" padding={4}>
                        <CircularProgress size="medium" />
                    </Box>
                )}

                {/* On a pricing-phase failure the error carries the rule trace that ran before it
                    threw; show both the diagnostics and the trace so the failing rule is in context. */}
                {!loading && error && (
                    <Stack gap={2} flex={1} minHeight={0} overflow="auto">
                        <PricingErrorPanel error={error} />
                        {error.nodes && error.nodes.length > 0 && (
                            <TraceNodeList nodes={error.nodes} />
                        )}
                    </Stack>
                )}

                {!loading && result && (
                    <Stack gap={2} flex={1} minHeight={0} overflow="auto">
                        <Stack direction="row" alignItems="baseline" gap={1}>
                            <Typography variant="h4">{result.unitPrice.toFixed(2)}</Typography>
                            <Typography variant="body1">{result.currency}</Typography>
                        </Stack>
                        <Stack direction="row" gap={1} flexWrap="wrap">
                            {Object.entries(result.byBucket).map(([bucket, amount]) => (
                                <Chip
                                    key={bucket}
                                    size="small"
                                    label={`${bucket}: ${amount.toFixed(2)}`}
                                />
                            ))}
                        </Stack>

                        <TraceNodeList nodes={result.nodes} />
                    </Stack>
                )}
            </Stack>
        </Drawer>
    );
}
